import { useQuery } from "@tanstack/react-query";
import { api } from "./api";
import type { MetricSources } from "./lib/metricAge";

export type Zone = {
  sport: string;       // "run" | "ride" | "swim" | "other"
  zone_type: string;   // "hr" | "power" | "pace" | "lt" | "lactate"
  zone_number: number; // 0..7
  min_value: number;
  max_value: number;
  unit: string;        // "bpm" | "watts" | "sec_per_km" | "sec_per_100m" | "mmol"
  source: string;      // "coach" | "ai" | "intervals"
  label: string | null;
};

export function useZones() {
  return useQuery<Zone[]>({
    queryKey: ["athlete-zones"],
    queryFn: async () => {
      const r = await api.get<Zone[]>("/athlete/zones");
      return r.data;
    },
  });
}

/**
 * ВЫВОДИМЫЕ зоны — посчитанные из порогов, а не назначенные.
 *
 * Отдельный тип и отдельный хук намеренно: у них нет `source`, потому что
 * никто их не задавал, и они не участвуют в цепочке приоритетов. Вместо
 * источника — якорь: порог, из которого набор получен. Смешивать их с
 * `Zone` нельзя даже в типах — иначе экран однажды покажет оценку там, где
 * пользователь ждёт решение тренера.
 */
export type DerivedZone = {
  sport: string;
  zone_type: string;
  zone_number: number;
  min_value: number;
  max_value: number;
  unit: string;
  label: string | null;
};

export type DerivedZoneSet = {
  sport: string;
  zone_type: string;
  /** cp | ftp | cs | css | lthr — из какого порога посчитано. */
  anchor: string;
  anchor_value: number;
  anchor_unit: string;
  zones: DerivedZone[];
};

export function useDerivedZones() {
  return useQuery<{ sets: DerivedZoneSet[] }>({
    queryKey: ["athlete-zones-derived"],
    queryFn: async () => {
      const r = await api.get<{ sets: DerivedZoneSet[] }>("/athlete/zones/derived");
      return r.data;
    },
  });
}

// GET /athlete/profile/sport-metrics → SportMetricsOut
// (api/routers/athlete_profile.py:1674). Only these fields are returned;
// per-sport threshold triptych derives the rest (VDOT / CS / T-pace) from
// zones, never fabricating a value the schema doesn't provide.
export type SportMetrics = {
  primary_sport: string;
  /** Виды, которые атлет указал сам. */
  disciplines: string[];
  /**
   * Виды, которыми он занимается ФАКТИЧЕСКИ (по завершённым тренировкам).
   * Экраны спрашивают объединение с `disciplines`: у Димы в профиле стоял
   * только бег при девятнадцати велотренировках за полгода, и всё вело на его
   * собственном экране выглядело чужим.
   */
  disciplines_seen: string[];
  ftp_watts: number | null;
  css_sec_per_100m: number | null;
  lthr_bpm: number | null;
  lthr_source: string | null;
  vo2max: number | null;
  /** Чей это VO₂max: garmin / vdot / age_rhr / threshold. */
  vo2max_source: string | null;
  /**
   * Источники спорят между собой. У Димы на 2026-08-03 разброс 16.5
   * пункта (garmin 54.0, расчёт по результату 44.1, формула от возраста
   * и пульса покоя 60.6) — движок помечает это сам, и показать одно
   * число из трёх молча значит выдать выбор за измерение.
   */
  vo2max_disagreement: boolean;
  vdot: number | null;
  critical_speed_sec_per_km: number | null;
  swolf: number | null;
  /** Вес из профиля (не из дневника самочувствия — там он часто пуст). */
  weight_kg: number | null;
  /** FTP на килограмм — то, чем мощность сравнивают между людьми. */
  watts_per_kg: number | null;
  /**
   * Когда вес измерен (ISO-дата) или null — возраст неизвестен.
   *
   * Вт/кг без этого — число неизвестной свежести: у Димы профильные 90 кг без
   * даты, а последняя запись веса в дневнике — за восемь месяцев до замера.
   */
  weight_measured_at: string | null;
  /**
   * Происхождение порогов (ab05): `{поле: {source, set_at}}`. Порог без даты —
   * порог неизвестного возраста, а зоны считаются от него. Возраст считает
   * `lib/metricAge` — не здесь: экранные тесты мокают этот модуль целиком.
   */
  metric_sources: MetricSources;
};

export function useSportMetrics() {
  return useQuery<SportMetrics>({
    queryKey: ["sport-metrics"],
    queryFn: async () =>
      (await api.get<SportMetrics>("/athlete/profile/sport-metrics")).data,
    staleTime: 60_000,
  });
}
